import { BlipMessage } from '../models'
import { store } from '@/store'
import { PostMessageProcessor } from '@/utils'
import { AppSettings } from '@/appSettings'
import { MonitoringErrorHandler } from 'monitoring-error-handler'

// Extends Notification api to has permission property on its type
// PR in: https://github.com/Microsoft/TypeScript/issues/14701
interface Notification extends EventTarget {
  close(): any
  onclick: () => void
  readonly permission: NotificationPermission
}

declare var Notification: {
  prototype: Notification;
  readonly permission: NotificationPermission;
  new(title: string, options?: NotificationOptions): Notification;
  requestPermission(callback?: NotificationPermissionCallback): Promise<NotificationPermission>;
}

export class BrowserNotification {
  documentTitleInterval: NodeJS.Timeout | undefined
  originalDocumentTitle: string

  constructor() {
    this.originalDocumentTitle = document.title

    //
    window.addEventListener('focus', () => this.resetDocumentTitle())
  }

  get botDisplayName() {
    return store.getters.displayName
  }

  /**
   *
   * Check broweser compatibility and permissions
   *
   */
  checkNotificationAvailability = () => (notificationFn: () => void) => {
      const spawnNotificationFn = notificationFn || undefined
      if ('Notification' in window) {
        switch (Notification.permission) {
          case 'granted':
            spawnNotificationFn()
            break
          case 'default':
            Notification.requestPermission((permission) => {
              // If the user accepts, let's create a notification
              if (permission === 'granted') {
                spawnNotificationFn()
              }
            })
            break
        }
      }
  }

  /**
   *
   * Send notification with params
   * @param options Notification API params
   *
   */
  sendNotification(message: BlipMessage, options?) {
    const plainTextMessage = this.getPlainTextFromMessage(message)

    // If pass on conditions to send notification to widget
    if (this.passOnCoditionsToWidget(message)) {
      this.sendPostMessageNotification(plainTextMessage)
    }

    try {
      this.checkNotificationAvailability()
        (() => {
          if (!this.passOnConditions(message)) {
            return
          }
          if (this.isHiddenMessage(message)) {
            return
          }

          const messageTitle = this.botDisplayName
          const customOptions = {
            icon: store.getters.botAccountData.photoUri,
            ...options,
          }

          customOptions.body = plainTextMessage

          //Browser notification
          const notification = new Notification(messageTitle, { ...customOptions })
          notification.onclick = () => {
            window.focus()
            notification.close()
          }

          navigator.serviceWorker.getRegistration().then(reg => {
            console.log(reg)

            if (reg) {
              reg.showNotification(messageTitle, { ...customOptions })
            }
          })

          // Change document title
          this.sendDocumentTitleNotification()
        })
    } catch (e) {
      console.error('Error when showing browser notification')
      MonitoringErrorHandler.instance.trackException({
          exception: e,
          handledAt: BrowserNotification.name,
          properties: {
            shortName: store.getters.botAccountData.identity,
            message: JSON.stringify(message) },
      })

    }
  }

  getPlainTextFromMessage(message) {
    switch (message.type) {
      case 'text/plain':
        return message.content
      default:
        return message.content.text ? message.content.text : 'Você recebeu uma nova mensagem'
    }
  }

  sendPostMessageNotification(message) {
    const botName = this.botDisplayName
    PostMessageProcessor.send({ code: AppSettings.get('PARENT_NOTIFICATION_CODE'), messageData: { message, botName } })
  }

  sendDocumentTitleNotification() {
    let count = 0
    if (!this.documentTitleInterval) {
      this.documentTitleInterval = setInterval(() => {
        document.title = count % 2 ? `${this.botDisplayName} diz...` : this.originalDocumentTitle
        count++
      }, 300)
    }
  }

  resetDocumentTitle() {
    clearInterval(this.documentTitleInterval)
    this.documentTitleInterval = undefined
    document.title = this.originalDocumentTitle
  }

  passOnConditions(message) {
    return message.direction === 'received'
      && !message.type.endsWith('chatstate+json')
      && document.hidden
  }

  isHiddenMessage(message) {
    return message.metadata['#blip.hiddenMessage']
      && message.metadata['#blip.hiddenMessage'].toLowerCase() === 'true'
  }

  passOnCoditionsToWidget(message) {
    return message.direction === 'received'
      && !message.type.endsWith('chatstate+json')
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./src/utils/BrowserNotification.ts